// Helpers de PREVISUALIZACIÓN de las plantillas de WhatsApp que (de mentira)
// dispara el SIMULADOR de autoescuela. Espejo de
// apps/driving-school/src/components/preview/templatePreview.js, adaptado al
// simulador: 100% mock, sin JWT ni CompanyConfigContext.
//
// Cada función devuelve { title, message, cta, … } con el MISMO texto que la
// plantilla que se enviaría de verdad en producción, con las variables sustituidas. Todo lo que va entre las marcas
// «templatePreview: cuerpo compartido» es IDÉNTICO al de la app (__tests__/plantillasPorEmpresa.copia.test.js): el texto
// sale del catálogo de plantillas (mensajes/plantillasAutoescuela.js) y acepta las plantillas A MEDIDA (`aMedida`).
//
// La variante depende del modo de la empresa (señal `conext`):
//   - conext === true  → modo Conext (sin número propio): plantilla _trial, que
//     antepone "📩 *{negocio}* te informa:" porque el remitente es el número
//     compartido de Conext. En la DEMO asumimos siempre modo Conext (true).
//   - conext === false → número propio (sin prefijo del negocio).
//
// El nombre del negocio sale del mock del simulador (datos fiscales del
// autoescuelaBillingStore → razon_social), con fallback "Autoescuela Demo".
import { getFiscal } from "../autoescuelaBillingStore";
// El texto de cada plantilla sale del CATÁLOGO (copia del de la app: el que manda c_sendTemplate) con vistaPlantilla(),
// y la propuesta de examen de la copia por valor de la app real (9/10/2026).
import { vistaPlantilla } from "../mensajes/plantillasAutoescuela";
import { mensajePropuesta, cuandoExamen, tipoExamen } from "../mensajes/examenPropuesta";
import { normalizaExamenLugar, paramsExamenLugar } from "../../../examenEspera/examenLugar";

// Nombre del negocio para el {{1}} del preview. En el simulador no hay JWT;
// usamos la razón social de los datos fiscales mock (editable en Configuración). Al entrar desde
// la ficha de un lead es el nombre de SU autoescuela (aplicarNombreLeadNegocio, en setSector).
export function getBusinessName() {
  try {
    const f = getFiscal();
    return (f && f.razon_social && String(f.razon_social).trim()) || "Autoescuela Demo";
  } catch {
    return "Autoescuela Demo";
  }
}

// Contexto de las respuestas de la vista previa INTERACTIVA (mensajes/respuestasBotones) para los avisos que el
// simulador manda de verdad al móvil del lead. «Quiero cambiar algo» abre el formulario con las horas libres de su
// profesor y la práctica se mueve sola: es lo que tiene configurado la autoescuela de demo (semana_cambio
// huecos + mover, aws_lambdas/v_simulatorSendPracticaSemana/reservaDemo.js). `origen: "asignada"` = una sola práctica.
export function ctxBotonesDemo({ origen = null, profesor = "", businessName } = {}) {
  return {
    conext: true,
    negocio: businessName || getBusinessName(),
    gracias: false,
    ...(origen ? { origen } : {}),
    cambio: { modo: "huecos", al_elegir: "mover", antelacion_horas: 24, dias_vista: 14, profesor: profesor || "" },
  };
}

// La semana con una línea por práctica CON IMAGEN (3/11/2026): poner a true cuando se encienda PRACTICA_SEMANA_LINEAS_IMG=1
// (plantillas practicas_semana_{n}_crm_trial_img APPROVED). Mientras, la vista previa enseña lo que sale: sin imagen.
const LINEAS_IMG_DEFECTO = true;

// ── templatePreview: cuerpo compartido (inicio) ──
// Catalán (8/10/2026, Garrotxa): solo por el número compartido; con número propio no existen.
const enCatalan = (idioma, conext) => idioma === "ca" && conext !== false;
const neg = (businessName) => businessName || getBusinessName();
const alumnoDef = (ca) => (ca ? "Alumne" : "Alumno");

const tipoLabel = (tipo) => (tipo === "practico" ? "práctico" : "teórico");

// Formatea una fecha ISO (YYYY-MM-DD) a DD/MM/YYYY como el envío real. Si ya viene
// en otro formato (label), la deja tal cual.
const fmtFecha = (f) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(f || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (f || "(fecha)");
};

/**
 * La vista de la plantilla `nombre` (la _trial que elige el envío) para la empresa: catálogo + a medida + idioma.
 * `extra` se añade al resultado (imagen, cta…). Exportada para otras vistas previas (horario del profesor…).
 */
export function previewPlantilla(title, nombre, { idioma = "es", conext = true, aMedida = null } = {}, vals = [], extra = {}) {
  const v = vistaPlantilla(nombre, { idioma: enCatalan(idioma, conext) ? "ca" : "es", vals, aMedida });
  if (!v) return null;
  return {
    title: v.aMedida ? `${title} · a medida` : title,
    ...(v.header ? { header: v.header } : {}),
    message: v.message,
    ...(v.footer ? { footer: v.footer } : {}),
    ...(v.buttons ? { buttons: v.buttons } : {}),
    cta: v.cta || v.flow || null,
    // El `cta` del catálogo es un botón de ENLACE (URL); el `flow`, un formulario: el móvil los pinta distinto.
    ...(v.cta ? { ctaUrl: true } : {}),
    plantilla: v.plantilla,
    idiomaPlantilla: v.idioma,
    aMedida: v.aMedida,
    ...extra,
  };
}

// Color y logo de la empresa en la imagen de cabecera (la misma que dibuja c_generateClaseConfirmacion).
const conMarca = (img, marca) =>
  (marca && (marca.color || marca.logo) ? { ...img, ...(marca.color ? { accent: marca.color } : {}), ...(marca.logo ? { logo: marca.logo } : {}) } : img);

export function previewEnlacePago({ conext = true, businessName, alumnoName, concepto, url, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  if (conext) {
    return previewPlantilla("Vista previa · Enlace de pago", "enlace_pago_crm_trial_v2", { idioma, conext, aMedida },
      [neg(businessName), alumnoName || alumnoDef(ca), concepto || (ca ? "el teu pagament" : "tu pago")]);
  }
  // Número propio: el enlace va en el cuerpo (la plantilla propia no lleva botón).
  return {
    title: "Vista previa · Enlace de pago",
    message: `Hola ${alumnoName || "Alumno"}, aquí tienes tu enlace para pagar ${concepto || "tu pago"}: ${url || "(enlace de pago)"}. Gracias.`,
    cta: null,
  };
}

// c_putExamen: {{3}} «práctico»/«pràctic», {{4}} DD/MM/AAAA, {{5}} « a las 10:00» (o nada).
// `lugar` (26/10/2026, Garrotxa): c_autoescuela_config.examen_lugar. Encendido (y la plantilla disponible: lo decide quien
// llama), el aviso sale con examen_recordatorio_lugar_crm_trial: cuándo, dónde y qué llevar (los MISMOS parámetros que
// c_putExamen y c_schedulerAlumnoAvisos, examenLugar.paramsExamenLugar).
export function previewExamenRecordatorio({ conext = true, businessName, alumnoName, tipo, prueba, fecha, hora, idioma, aMedida, lugar = null, lugarExamen = null, permiso = null } = {}) {
  const ca = enCatalan(idioma, conext);
  if (conext && normalizaExamenLugar(lugar).enabled) {
    const p = paramsExamenLugar({ examen: { tipo, prueba, fecha, hora, lugar: lugarExamen }, nombre: alumnoName || alumnoDef(ca), cfg: lugar, lang: ca ? "ca" : "es", permiso });
    return previewPlantilla("Vista previa · Recordatorio de examen", "examen_recordatorio_lugar_crm_trial", { idioma, conext, aMedida }, [neg(businessName), ...p]);
  }
  if (conext) {
    return previewPlantilla("Vista previa · Recordatorio de examen", "examen_recordatorio_crm_trial_v2", { idioma, conext, aMedida },
      [neg(businessName), alumnoName || alumnoDef(ca), tipoExamen(tipo, ca ? "ca" : "es"), fmtFecha(fecha), hora ? (ca ? ` a les ${hora}` : ` a las ${hora}`) : ""]);
  }
  return {
    title: "Vista previa · Recordatorio de examen",
    message: `Hola ${alumnoName || "Alumno"}, te recordamos tu examen ${tipoLabel(tipo)} el ${fmtFecha(fecha)}${hora ? ` a las ${hora}` : ""}. ¡Mucha suerte!`,
    cta: null,
  };
}

// Propuesta de examen con «Sí, voy» / «No puedo» (9/10/2026; examen_propuesta_crm_trial, solo número compartido).
export function previewExamenPropuesta({ conext = true, businessName, alumnoName, tipo, fecha, hora, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  if (conext) {
    const l = ca ? "ca" : "es";
    return previewPlantilla("Vista previa · Propuesta de examen", "examen_propuesta_crm_trial", { idioma, conext, aMedida },
      [neg(businessName), alumnoName || alumnoDef(ca), tipoExamen(tipo, l), cuandoExamen({ tipo, fecha, hora }, l)]);
  }
  const m = mensajePropuesta({ conext, negocio: neg(businessName), nombre: alumnoName, examen: { tipo, fecha, hora }, lang: idioma });
  return { title: "Vista previa · Propuesta de examen", message: m.message, buttons: m.buttons, cta: null };
}

// c_putExamen.palabrasExamen: APTO / NO APTO / NO PRESENTADO (APTE / NO APTE / NO PRESENTAT).
const RESULTADO = {
  es: { apto: "APTO", no_apto: "NO APTO", no_presentado: "NO PRESENTADO" },
  ca: { apto: "APTE", no_apto: "NO APTE", no_presentado: "NO PRESENTAT" },
};
const resultadoTxt = (resultado, l) => {
  const r = String(resultado || "");
  const clave = { APTO: "apto", "NO APTO": "no_apto", "NO PRESENTADO": "no_presentado" }[r] || r;
  return RESULTADO[l][clave] || r || "(resultado)";
};

export function previewExamenResultado({ conext = true, businessName, alumnoName, tipo, resultado, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  if (conext) {
    const l = ca ? "ca" : "es";
    return previewPlantilla("Vista previa · Resultado de examen", "examen_resultado_crm_trial_v2", { idioma, conext, aMedida },
      [neg(businessName), alumnoName || alumnoDef(ca), tipoExamen(tipo, l), resultadoTxt(resultado, l)]);
  }
  return {
    title: "Vista previa · Resultado de examen",
    message: `Hola ${alumnoName || "Alumno"}, ya tenemos el resultado de tu examen de conducir ${tipoLabel(tipo)}. El resultado ha sido: *${resultadoTxt(resultado, "es")}*. Para cualquier duda, no dudes en contactar con nosotros.`,
    cta: null,
  };
}

// c_sendPracticaSchedule (weekly_digest): con imagen por el número compartido; `botones` → *_img_btn («Me viene bien» /
// «Quiero cambiar algo»). `lineasModo` (9/10/2026, Elices): practicas_semana_{n}_crm_trial, cada práctica en su línea y
// sin imagen (utils/semanaMensaje.usaLineas decide, como el envío). `clases`/`rango` (opcionales) afinan la imagen.
// `semanaLineasImg` (3/11/2026): con PRACTICA_SEMANA_LINEAS_IMG=1 las líneas van con la imagen de la semana en la cabecera
// (practicas_semana_{n}_crm_trial_img, mismo texto y botones).
export function previewPracticasSemana({ conext = true, businessName, alumnoName, lineas, botones = false, clases, rango, idioma, lineasModo = false, aMedida, marca, semanaLineasImg = LINEAS_IMG_DEFECTO } = {}) {
  const ca = enCatalan(idioma, conext);
  const tiene = Array.isArray(lineas) && lineas.length > 0;
  const nombre = alumnoName || alumnoDef(ca);
  const imagenSemana = (lineasImg) => conMarca({
    kind: "semana",
    lineas: lineasImg,
    ...(alumnoName ? { alumno: alumnoName } : {}),
    ...(clases ? { clases } : {}),
    ...(rango ? { rango } : {}),
    ...(ca ? { lang: "ca" } : {}), // la imagen real también va en catalán (c_generateClaseConfirmacion lang "ca")
  }, marca);
  if (conext && lineasModo && botones && tiene) {
    if (semanaLineasImg) {
      return previewPlantilla("Vista previa · Resumen semanal", `practicas_semana_${lineas.length}_crm_trial_img`, { idioma, conext, aMedida },
        [neg(businessName), nombre, ...lineas], { imagen: imagenSemana(lineas) });
    }
    return previewPlantilla("Vista previa · Resumen semanal", `practicas_semana_${lineas.length}_crm_trial`, { idioma, conext, aMedida },
      [neg(businessName), nombre, ...lineas]);
  }
  const lineasOk = tiene ? lineas : ["🗓️ Lun 12:00 · Ciudad · Tu instructor"];
  const lista = lineasOk.join(" • ");
  if (conext) {
    return previewPlantilla("Vista previa · Resumen semanal", botones ? "practicas_semana_crm_trial_img_btn" : "practicas_semana_crm_trial_img",
      { idioma, conext, aMedida }, [neg(businessName), nombre, lista], {
        // Por el número compartido el aviso lleva la imagen de la semana en la cabecera.
        imagen: imagenSemana(lineasOk),
      });
  }
  return {
    title: "Vista previa · Resumen semanal",
    message: `Hola ${nombre} 👋\n\nEstas son tus prácticas programadas para esta semana:\n\n${lista}\n\nPara cualquier cambio o cancelación, contáctanos por este chat.`,
    cta: null,
  };
}

// Semana al PROFESOR (semana_profesor_crm_trial, 5/10/2026): solo existe por el número compartido. El botón abre su
// página de solo lectura con «Avisar de un impedimento».
export function previewSemanaProfesor({ conext = true, businessName, profesor, desde, hasta, practicas, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  const n = Number(practicas) || 0;
  if (conext) {
    return previewPlantilla("Vista previa · Semana al profesor", "semana_profesor_crm_trial", { idioma, conext, aMedida }, [
      neg(businessName), profesor || (ca ? "Òscar" : "Óscar"),
      desde || (ca ? "dilluns 12/10" : "lunes 6/10"), hasta || (ca ? "diumenge 18/10" : "domingo 12/10"),
      ca ? `${n} ${n === 1 ? "pràctica" : "pràctiques"}` : `${n} ${n === 1 ? "práctica" : "prácticas"}`,
    ]);
  }
  return {
    title: "Vista previa · Semana al profesor",
    message: `Planificación semanal lista ✅\n\nHola ${profesor || "Óscar"}, tu semana del ${desde || "lunes 6/10"} al ${hasta || "domingo 12/10"} tiene ${n} ${n === 1 ? "práctica" : "prácticas"} en total.\n\nPulsa el botón para ver cada práctica con su hora, alumno y coche, y avisar si algún día no puedes dar clase.`,
    cta: "Ver mi semana",
    ctaUrl: true, // botón de enlace a su página de la semana, no una respuesta rápida
  };
}

// c_sendPracticaSchedule (individual): {{3}} DD/MM/AAAA, {{4}} la hora con los datos de la autoescuela («17:00 hasta las
// 18:30 (2 clases)», utils/datosPractica.horaAviso), {{5}} el tipo. Con imagen por el número compartido; `botones` →
// *_img_btn. `horaImagen`: la franja de la imagen («17:00 – 18:30»), si se sabe. `profesor`: el nombre del profesor de la
// práctica tal cual lo pinta la imagen real (instructor_name); sin él la imagen enseña «Tu profesor».
export function previewPracticaAsignada({ conext = true, businessName, alumnoName, tipo, fecha, hora, horaImagen, profesor, botones = false, idioma, aMedida, marca } = {}) {
  const ca = enCatalan(idioma, conext);
  const tipoTxt = tipo || (ca ? "Ciutat" : "Ciudad");
  if (conext) {
    return previewPlantilla("Vista previa · Práctica asignada", botones ? "practica_asignada_crm_trial_img_btn" : "practica_asignada_crm_trial_img",
      { idioma, conext, aMedida }, [neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", tipoTxt], {
        imagen: conMarca({ kind: "asignada", fecha: fmtFecha(fecha), hora: horaImagen || hora || "", tipo: tipoTxt, alumno: alumnoName || alumnoDef(ca), ...(String(profesor || "").trim() ? { profesor: String(profesor).trim() } : {}), ...(ca ? { lang: "ca" } : {}) }, marca),
      });
  }
  return {
    title: "Vista previa · Práctica asignada",
    message: `Hola ${alumnoName || "Alumno"},\n\nSe le ha asignado una práctica de ${tipo || "ciudad"} el ${fmtFecha(fecha)} a las ${hora || "(hora)"}.\n\n¡Te esperamos!`,
    cta: null,
  };
}

// c_sendPracticaSchedule (modification): {{3}} «práctica de Ciudad», fechas DD/MM/AAAA y horas HH:MM. `botones` (casilla
// «Con botones», 4/10/2026): sin botones es practica_modificada_crm_trial_sinbtn. Solo por el número compartido: con
// número propio sale siempre con botones. `motivo` (14/10/2026, Arribas): con texto, practica_modificada_motivo_crm_trial(_sinbtn)
// con «Motivo: …» en {{8}} (c_sendPracticaSchedule con PRACTICA_MOTIVO_CAMBIO=1; quien llama decide si está disponible).
export function previewPracticaModificada({ conext = true, businessName, alumnoName, tipo, fechaAnt, horaAnt, fechaNueva, horaNueva, botones = true, idioma, aMedida, motivo = "" } = {}) {
  const ca = enCatalan(idioma, conext);
  const sinBotones = conext && botones === false;
  const motivoTxt = String(motivo || "").replace(/\s+/g, " ").trim();
  if (conext) {
    const base = motivoTxt ? "practica_modificada_motivo_crm_trial" : "practica_modificada_crm_trial";
    return previewPlantilla("Vista previa · Práctica modificada", sinBotones ? `${base}_sinbtn` : base,
      { idioma, conext, aMedida }, [
        neg(businessName), alumnoName || alumnoDef(ca), tipo || (ca ? "pràctica" : "práctica"),
        fechaAnt || (ca ? "(data)" : "(fecha)"), horaAnt || "(hora)", fechaNueva || (ca ? "(data)" : "(fecha)"), horaNueva || "(hora)",
        ...(motivoTxt ? [motivoTxt] : []),
      ]);
  }
  return {
    title: "Vista previa · Práctica modificada",
    message: `Hola ${alumnoName || "Alumno"},\n\nTu ${tipo || "práctica"} prevista para el ${fechaAnt || "(fecha)"} a las ${horaAnt || "(hora)"} ha sido modificada.\n\n📅 Nueva fecha: ${fechaNueva || "(fecha)"} a las ${horaNueva || "(hora)"}\n\n¿Nos confirmas tu asistencia?`,
    buttons: ["Confirmar práctica", "Cancelar práctica"],
    cta: null,
  };
}

// c_cancelPractica (cancel_manual → practica_cancel_crm_trial): {{2}} el nombre DE PILA del alumno (9/10/2026), {{3}} la
// fecha larga («jueves, 8 de octubre», utils/perfilAvisos.fechaLargaAviso), {{4}} HH:MM, {{5}} el motivo («no indicado» /
// «no s'ha indicat» sin él: c_cancelPractica.paramsAvisoCancelacion).
// `tipo` se acepta por compatibilidad con los llamadores, pero la plantilla no lo usa.
export function previewPracticaCancel({ conext = true, businessName, alumnoName, tipo, fecha, hora, motivo, idioma, aMedida } = {}) {
  void tipo;
  const ca = enCatalan(idioma, conext);
  const pila = String(alumnoName || "").trim().split(/\s+/)[0] || "";
  const motivoTxt = String(motivo || "").trim() || (ca ? "no s'ha indicat" : "no indicado");
  if (conext) {
    return previewPlantilla("Vista previa · Práctica cancelada", "practica_cancel_crm_trial", { idioma, conext, aMedida },
      [neg(businessName), pila || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", motivoTxt]);
  }
  return {
    title: "Vista previa · Práctica cancelada",
    header: "PRÁCTICA CANCELADA",
    message: `Hola ${pila || "Alumno"},\n\nSu práctica del ${fmtFecha(fecha)} a las ${hora || "(hora)"} ha sido cancelada.\n\nMotivo: ${motivoTxt}\n\nPara reprogramarla, contacte con nosotros.`,
    cta: null,
  };
}

// Recordatorio de práctica (c_sendPracticaReminder.pickTemplateName, misma regla): sin botones → _sinbtn (manda);
// «Solo Confirmar» → _conf (gana a «Cambiar hora»); «Pedir clase» con su botón → _v2 («Cambiar hora»); si no, la de
// siempre. Las tres variantes solo por el número compartido. {{4}} = la hora con los datos de la autoescuela (con el
// tipo dentro: utils/datosPractica.horaAviso con incluirTipo).
export function previewPracticaRecordatorio({ conext = true, businessName, alumnoName, fecha, hora, cambioHora = false, botones = true, soloConfirmar = false, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  if (conext) {
    const name = botones === false ? "practica_recordatorio_crm_trial_sinbtn"
      : soloConfirmar === true ? "practica_recordatorio_crm_trial_conf"
        : cambioHora ? "practica_recordatorio_crm_trial_v2" : "practica_recordatorio_crm_trial";
    return previewPlantilla("Vista previa · Recordatorio de práctica", name, { idioma, conext, aMedida },
      [neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)"]);
  }
  return {
    title: "Vista previa · Recordatorio de práctica",
    header: "RECORDATORIO DE PRÁCTICA",
    message: `Hola ${alumnoName || "Alumno"},\n\nLe recordamos que tiene una práctica el ${fmtFecha(fecha)} a las ${hora || "(hora)"}.\n\n¿Nos confirma su asistencia?`,
    buttons: ["Confirmar práctica", "Cancelar práctica"],
    cta: null,
  };
}

// Factura al alumno (factura_cliente_crm → factura_alumno_autoescuela_trial por el número de avisos). La cabecera es el
// PDF: aquí se enseña su rótulo.
export function previewFacturaAlumno({ conext = true, businessName, alumnoName, numero, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  const nombre = alumnoName || alumnoDef(ca);
  const num = numero || "FAC-2026-0007";
  if (conext) {
    return previewPlantilla("Vista previa · Factura", "factura_alumno_autoescuela_trial", { idioma, conext, aMedida },
      [neg(businessName), nombre, num], { header: ca ? "📄 Factura (PDF adjunt)" : "📄 Factura (PDF adjunto)" });
  }
  return {
    title: "Vista previa · Factura",
    header: "📄 Factura (PDF adjunto)",
    message: `Hola ${nombre}, te adjuntamos tu factura ${num}. ¡Gracias!`,
    cta: null,
  };
}

// ── Plantillas de los avisos configurables (solo existen por el número compartido: las _trial) ──

// Rellenar huecos: practica_hueco_libre_trial, con botones «Lo quiero» / «Ahora no».
export function previewPracticaHuecoLibre({ conext = true, businessName, alumnoName, tipo, fecha, hora, profesor, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Hueco libre", "practica_hueco_libre_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), tipo || (ca ? "Ciutat" : "Ciudad"), fmtFecha(fecha), hora || "(hora)",
    profesor || (ca ? "el teu professor" : "tu profesor"),
  ]);
}

// Cancelación tardía cobrada: practica_cargo_cancelacion_trial.
export function previewPracticaCargoCancelacion({ conext = true, businessName, alumnoName, fecha, hora, horas, importe, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Cancelación tardía", "practica_cargo_cancelacion_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", String(horas || 24), importe || (ca ? "(import)" : "(importe)"),
  ]);
}

// «Pierde la clase» SIN bono (19/10/2026, Carian): practica_cancelacion_pierde_trial (es + ca), un solo mensaje, sin cargo.
export function previewPracticaCancelacionPierde({ conext = true, businessName, alumnoName, fecha, hora, horas, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Pierde la clase", "practica_cancelacion_pierde_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", String(horas || 24),
  ]);
}

// Cobrado con la tarjeta guardada (10/10/2026): cancelación tardía (practica_cancelacion_cobrada_trial) y no presentado
// (practica_no_presentado_cobrada_trial); y el cargo apuntado en su cuenta por no presentarse
// (practica_no_presentado_cargo_trial). Solo existen las _trial (es + ca).
export function previewPracticaCancelacionCobrada({ conext = true, businessName, alumnoName, fecha, hora, horas, importe, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Cobrado en su tarjeta", "practica_cancelacion_cobrada_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", String(horas || 24), importe || (ca ? "(import)" : "(importe)"),
  ]);
}
export function previewNoPresentadoCobrada({ conext = true, businessName, alumnoName, fecha, hora, importe, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · No presentado, cobrado en su tarjeta", "practica_no_presentado_cobrada_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", importe || (ca ? "(import)" : "(importe)"),
  ]);
}
export function previewNoPresentadoCargo({ conext = true, businessName, alumnoName, fecha, hora, importe, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · No presentado, cargo en su cuenta", "practica_no_presentado_cargo_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), fmtFecha(fecha), hora || "(hora)", importe || (ca ? "(import)" : "(importe)"),
  ]);
}

// Bono agotándose: bono_practicas_agotandose_trial_v2. MARKETING en Meta; sin catalán.
export function previewBonoAgotandose({ conext = true, businessName, alumnoName, quedan, bono, aMedida } = {}) {
  return previewPlantilla("Vista previa · Bono agotándose", "bono_practicas_agotandose_trial_v2", { idioma: "es", conext, aMedida },
    [neg(businessName), alumnoName || "Alumno", String(quedan ?? 2), bono || "de 10 prácticas"]);
}

// Caducidad del CAP: cap_caducidad_alumno_trial. MARKETING en Meta; sin catalán.
export function previewCapCaducidad({ conext = true, businessName, alumnoName, fecha, aMedida } = {}) {
  return previewPlantilla("Vista previa · Caducidad del CAP", "cap_caducidad_alumno_trial", { idioma: "es", conext, aMedida },
    [neg(businessName), alumnoName || "Alumno", fecha || "15/03/2027"]);
}

// Petición de clase descartada o caducada: practica_peticion_no_atendida_trial.
export function previewPeticionNoAtendida({ conext = true, businessName, alumnoName, motivo, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Petición no atendida", "practica_peticion_no_atendida_trial", { idioma, conext, aMedida },
    [neg(businessName), alumnoName || alumnoDef(ca), motivo || (ca ? "no queden forats aquesta setmana" : "no quedan huecos esta semana")]);
}

// Bienvenida al alumno nuevo: bienvenida_alumno_crm_trial. Solo existe por el número de avisos.
export function previewBienvenidaAlumno({ conext = true, businessName, alumnoName, permiso, norma, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Bienvenida al alumno", "bienvenida_alumno_crm_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), permiso || "B",
    norma || (ca ? "si no pots venir, avisa amb més de 24 hores; si no, la pràctica es cobra." : "si no puedes venir, avisa con más de 24 horas; si no, la práctica se cobra."),
  ]);
}
// Bienvenida CON EL ACCESO A LA TEÓRICA (14/10/2026, Autoescola Wins): la misma + «Curso de teoría en {{5}}: {{6}}».
// {{5}} = plataforma (y enlace) y {{6}} = «usuario X · contraseña Y», como los compone accesoTeorica.js al enviar.
export function previewBienvenidaTeorica({ conext = true, businessName, alumnoName, permiso, norma, idioma, aMedida, plataforma, acceso } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Bienvenida con la teórica", "bienvenida_teorica_alumno_crm_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), permiso || "B",
    norma || (ca ? "si no pots venir, avisa amb més de 24 hores; si no, la pràctica es cobra." : "si no puedes venir, avisa con más de 24 horas; si no, la práctica se cobra."),
    plataforma || "iEOL",
    acceso || (ca ? "usuari jordi.puig · contrasenya Kq73pa" : "usuario carlos.garcia · contraseña Kq73pa"),
  ]);
}
// Bienvenida CON EL BOTÓN DE DISPONIBILIDAD en un solo mensaje (22/10/2026, Ribas #30c): mismas variables que la normal
// + el botón «Indicar disponibilidad», que abre el mismo formulario que «Pedir disponibilidad».
export function previewBienvenidaDisponibilidad({ conext = true, businessName, alumnoName, permiso, norma, idioma, aMedida } = {}) {
  const ca = enCatalan(idioma, conext);
  return previewPlantilla("Vista previa · Bienvenida con su disponibilidad", "bienvenida_disponibilidad_alumno_crm_trial", { idioma, conext, aMedida }, [
    neg(businessName), alumnoName || alumnoDef(ca), permiso || "B",
    norma || (ca ? "si no pots venir, avisa amb més de 24 hores; si no, la pràctica es cobra." : "si no puedes venir, avisa con más de 24 horas; si no, la práctica se cobra."),
  ]);
}
// ── templatePreview: cuerpo compartido (fin) ──
